import {
  Alert, Card, Container, Group, Loader, NumberInput, Select, Stack, Switch, Text, Title,
} from "@mantine/core";
import { IconCoin } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getEnrichmentSettings, updateEnrichmentSettings, getEnrichmentCostBasis } from "../api";

const DAYS_PER_MONTH = 30.44;

/**
 * Approximate recurring monthly cost of the LinkedIn refresh, computed live from the current
 * (possibly unsaved) settings so the effect of the interval + the web-search toggle is immediate.
 * Headline counts LinkedIn fetch (+ web search when included), at the discounted refresh price;
 * embedding is occasional (only when a profile changes) and intentionally left out of the headline.
 */
function monthlyEstimate(settings, cb) {
  if (!cb || !settings.autoLinkedInRefresh || !settings.linkedInRefreshDays) return null;
  const refreshesPerMonth = DAYS_PER_MONTH / settings.linkedInRefreshDays;
  const includesWeb = settings.autoWebSearch && settings.autoWebSearchOnLinkedInRefresh;
  const perRefresh = cb.linkedInFetchCredits + (includesWeb ? cb.webSearchCredits : 0);
  // Round the per-refresh billed amount the same way the backend does (Math.Ceiling on the
  // discounted cost), so the estimate matches the actual charge.
  const perRefreshBilled = Math.ceil(perRefresh * Number(cb.refreshPriceMultiplier));
  const credits = cb.eligibleLinkedInCount * refreshesPerMonth * perRefreshBilled;
  // Default to EUR for now (primary market); the credit→EUR rate carries its own margin.
  const eur = (credits / 1000) * Number(cb.eurPer1000Credits);
  return { credits: Math.round(credits), eur, count: cb.eligibleLinkedInCount };
}

/**
 * NumberInput that keeps a local draft value and only commits on blur (or
 * Enter), so stepper-clicks and typing don't fire a PUT per keystroke.
 */
function SavedNumberInput({ label, persisted, onSave, min, max, disabled, w }) {
  const [value, setValue] = useState(persisted);

  // Reset local draft when the persisted value changes (e.g. another save).
  useEffect(() => setValue(persisted), [persisted]);

  function commit() {
    const next = Number(value);
    if (!Number.isFinite(next) || next === persisted) return;
    if (min != null && next < min) return;
    if (max != null && next > max) return;
    onSave(next);
  }

  return (
    <NumberInput
      label={label}
      value={value}
      onChange={setValue}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
      min={min}
      max={max}
      disabled={disabled}
      w={w}
    />
  );
}

export default function EnrichmentSettingsPage() {
  const { t } = useTranslation("Veft");
  const [settings, setSettings] = useState(null);
  const [costBasis, setCostBasis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getEnrichmentSettings()
      .then((s) => setSettings(s))
      .finally(() => setLoading(false));
    getEnrichmentCostBasis().then(setCostBasis).catch(() => {});
  }, []);

  async function save(patch) {
    setSaving(true);
    try {
      const updated = await updateEnrichmentSettings(patch);
      setSettings(updated);
      notifications.show({ title: t("saved"), message: t("settings_saved"), color: "green" });
    } catch {
      notifications.show({ title: t("error"), message: t("save_failed"), color: "red" });
    } finally {
      setSaving(false);
    }
  }

  if (loading || !settings) return <Loader />;

  return (
    <Container size="md" py="xl">
      <Title order={2} mb="lg">{t("enrichment_settings")}</Title>

      <Stack gap="md">
        <Card withBorder>
          <Stack gap="sm">
            <Title order={5}>{t("default_language")}</Title>
            <Text size="sm" c="dimmed">
              {t("default_language_help")}
            </Text>
            <Select
              data={[
                { value: "de", label: "Deutsch" },
                { value: "en", label: "English" },
              ]}
              value={settings.defaultLanguage ?? "de"}
              onChange={(value) => value && save({ defaultLanguage: value })}
              disabled={saving}
              w={240}
            />
          </Stack>
        </Card>

        <Card withBorder>
          <Stack gap="sm">
            <Title order={5}>{t("linkedin_refresh")}</Title>
            <Switch
              label={t("auto_linkedin_refresh")}
              checked={settings.autoLinkedInRefresh}
              onChange={(e) => save({ autoLinkedInRefresh: e.currentTarget.checked })}
              disabled={saving}
            />
            <SavedNumberInput
              label={t("linkedin_refresh_days")}
              persisted={settings.linkedInRefreshDays}
              onSave={(v) => save({ linkedInRefreshDays: v })}
              min={7}
              max={365}
              disabled={saving}
              w={200}
            />
            <SavedNumberInput
              label={t("max_refresh_per_cycle")}
              persisted={settings.maxRefreshPerCycle}
              onSave={(v) => save({ maxRefreshPerCycle: v })}
              min={1}
              max={500}
              disabled={saving}
              w={200}
            />
            {(() => {
              if (!settings.autoLinkedInRefresh)
                return <Text size="xs" c="dimmed">{t("cost_estimate_off")}</Text>;
              const est = monthlyEstimate(settings, costBasis);
              if (!est) return null;
              return (
                <Alert color="gray" variant="light" icon={<IconCoin size={16} />} p="xs">
                  <Group gap={6}>
                    <Text size="sm" fw={600}>
                      {t("cost_estimate_value", {
                        credits: est.credits.toLocaleString("de-DE"),
                        eur: est.eur.toFixed(2),
                      })}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {t("cost_estimate_help", { count: est.count.toLocaleString("de-DE") })}
                  </Text>
                </Alert>
              );
            })()}
          </Stack>
        </Card>

        <Card withBorder>
          <Stack gap="sm">
            <Title order={5}>{t("web_search")}</Title>
            <Switch
              label={t("auto_web_search")}
              checked={settings.autoWebSearch}
              onChange={(e) => save({ autoWebSearch: e.currentTarget.checked })}
              disabled={saving}
            />
            <Switch
              label={t("auto_web_search_on_linkedin_refresh")}
              description={t("auto_web_search_on_linkedin_refresh_help")}
              checked={settings.autoWebSearchOnLinkedInRefresh}
              onChange={(e) => save({ autoWebSearchOnLinkedInRefresh: e.currentTarget.checked })}
              disabled={saving || !settings.autoWebSearch}
            />
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
